// ============================================================
// VCS.InterpretSession -- Ansicht + Ablauf einer Dolmetscher-Sitzung mit
// Partner-Geraet (2026-10-16)
// ============================================================
// Gemeinsam fuer Messenger (Gastgeber, eingeladener Kontakt) und Gast-Seite
// (guest-interpret.php). Markup: includes/interpret-screen.php.
//
// Grundregel wie auf dem Server: dieses Geraet hat EINE Sprache. Gross steht,
// was die anderen gesagt haben, in meiner Sprache. Klein darunter das
// Original. Was ich selbst gesagt habe, steht als "Du: ..." darunter -- so
// sehe ich, wie ich verstanden wurde.
//
// Der Aufrufer liefert den Transport:
//   send(obj), sendBinary(buffer)
// und optional Rueckrufe:
//   onEnded(reason)            Sitzung vorbei (Overlay ist dann zu)
//   onInviteLink(), onInviteContact()   nur Gastgeber
// ============================================================
window.VCS = window.VCS || {};

VCS.InterpretSession = class {

    static SPEAK_STORAGE_KEY = 'interpretSpeak';
    static OUTPUT_MAX_PX = 40;
    static OUTPUT_MIN_PX = 22;
    static LISTEN_AGAIN_MS = 1500;

    constructor(transport, options = {}) {
        this._send       = transport.send;
        this._sendBinary = transport.sendBinary;
        this._opts       = options;
        this._audio      = new VCS.InterpretAudio((buf) => this._sendBinary(buf));

        this._active     = false;
        this._roomId     = null;
        this._mode       = 'pair';
        this._role       = 'guest';
        this._you        = null;
        this._myLang     = 'de';
        this._members    = [];
        this._pending    = [];
        this._hostName   = '';
        this._floor      = null;

        this._micWanted  = false;
        this._micReady   = false;
        this._micReason  = '';
        this._speak      = true;
        this._playing    = false;
        this._resumeMic  = false;

        this._originals  = new Map();   // seq -> Original (bis die Uebersetzung kommt)
        this._listenTimer = null;
        this._qrUrl      = '';
        this._view       = null;
        this._bound      = false;
    }

    // -- Oeffnen / Schliessen -------------------------------------

    get active() {
        return this._active;
    }

    get roomId() {
        return this._roomId;
    }

    get role() {
        return this._role;
    }

    // role: 'host' | 'user' | 'guest'
    open({ roomId, mode, role, lang, hostName }) {
        this._bindView();
        this._active   = true;
        this._roomId   = roomId || null;
        this._mode     = mode === 'group' ? 'group' : 'pair';
        this._role     = role || 'guest';
        this._myLang   = lang || this._myLang;
        this._hostName = hostName || '';
        this._members  = [];
        this._pending  = [];
        this._loadSpeak();

        const v = this._view;
        v.inviteBar.classList.toggle('hidden', this._role !== 'host');
        v.brand?.classList.toggle('hidden', this._role !== 'guest');
        v.close.setAttribute('aria-label', t(this._role === 'host' ? 'interpretConfirmEnd' : 'interpretConfirmLeave'));
        this._fillLangSelect();
        this._setOutput('', '', '');
        v.own.textContent = '';
        v.own.classList.add('hidden');
        this._hideQr();
        v.membersList.classList.add('hidden');
        v.overlay.classList.remove('hidden');
        this._render();
        console.info('[interpret] Sitzung offen room=' + (roomId || '-') + ' mode=' + this._mode + ' role=' + this._role + ' lang=' + this._myLang);
    }

    // Raum-ID kommt beim Gastgeber erst nach dem Erzeugen des Links.
    setRoom(roomId, mode) {
        this._roomId = roomId;
        if (mode) {
            this._mode = mode === 'group' ? 'group' : 'pair';
        }
        this._render();
    }

    // Lokal aufraeumen. Der Server erfaehrt es ueber interpret-leave (close())
    // oder hat selbst beendet (interpret-ended).
    teardown(reason) {
        if (!this._active) {
            return;
        }
        this._active    = false;
        this._micWanted = false;
        this._micReady  = false;
        this._resumeMic = false;
        clearTimeout(this._listenTimer);
        this._audio.release();
        this._originals.clear();
        if (this._view) {
            this._view.overlay.classList.add('hidden');
            this._hideQr();
        }
        console.info('[interpret] Sitzung zu reason=' + (reason || '-'));
        this._opts.onEnded?.(reason || 'closed', this._roomId);
        this._roomId = null;
    }

    async requestClose() {
        const key = this._role === 'host' ? 'interpretConfirmEnd' : 'interpretConfirmLeave';
        const ok = await this._confirm(t(key));
        if (!ok) {
            return;
        }
        this._send({ type: 'interpret-leave' });
        this.teardown('left');
    }

    // Verbindung weg: Mikro nicht mehr senden, nach dem Wiederverbinden
    // macht der Aufrufer den Raum wieder auf (interpret-host/-join/-knock).
    onConnectionLost() {
        if (!this._active) {
            return;
        }
        this._resumeMic = this._micWanted;
        this._micReady = false;
        this._audio.setStreaming(false);
        this._setStatus(t('interpretErrConnection'), 'error');
    }

    // -- Nachrichten vom Server -----------------------------------

    handle(msg) {
        if (!this._active || !msg) {
            return;
        }
        if (msg.roomId && this._roomId && msg.roomId !== this._roomId) {
            return;
        }
        switch (msg.type) {
            case 'interpret-room':        this._onRoom(msg); break;
            case 'interpret-joined':      this._onJoined(msg); break;
            case 'interpret-transcript':  this._onTranscript(msg); break;
            case 'interpret-translated':  this._onTranslated(msg); break;
            case 'interpret-audio':       this._onAudio(msg); break;
            case 'interpret-audio-end':   break;
            case 'interpret-mic-state':   this._onMicState(msg); break;
            case 'interpret-ended':       this._onEnded(msg); break;
            case 'interpret-error':       this._onError(msg); break;
            default: break;
        }
    }

    _onRoom(msg) {
        const before = new Map(this._members.map(m => [m.id, m]));
        this._members  = Array.isArray(msg.members) ? msg.members : [];
        this._pending  = Array.isArray(msg.pending) ? msg.pending : this._pending;
        this._you      = msg.you || this._you;
        this._mode     = msg.mode === 'group' ? 'group' : 'pair';
        this._hostName = msg.hostName || this._hostName;
        this._floor    = msg.floor || null;
        this._roomId   = msg.roomId || this._roomId;

        // Kommen und Gehen kurz melden.
        for (const m of this._members) {
            if (m.id !== this._you && !before.has(m.id) && before.size) {
                this._toast(t('interpretPartnerJoined').replace('{name}', m.name || t('guest')));
            }
        }
        for (const [id, m] of before) {
            if (id !== this._you && !this._members.some(x => x.id === id)) {
                this._toast(t('interpretPartnerLeft').replace('{name}', m.name || t('guest')));
            }
        }
        // Jemand ist da -- der QR-Code hat seinen Zweck erfuellt (Gespraech).
        if (this._mode === 'pair' && this._others().length) {
            this._hideQr();
        }
        this._syncAfterAttach();
        this._render();
    }

    // Nach dem (Wieder-)Eintritt in den Raum: Einstellungen nachreichen, die
    // der Server vorher nicht zuordnen konnte.
    _syncAfterAttach() {
        if (!this._speak && !this._speakSent) {
            this._speakSent = true;
            this._send({ type: 'interpret-speak', on: false });
        }
        if (this._resumeMic) {
            this._resumeMic = false;
            this._send({ type: 'interpret-mic', on: true });
        }
    }

    _onJoined(msg) {
        this._you      = msg.you || this._you;
        this._myLang   = msg.lang || this._myLang;
        this._hostName = msg.hostName || this._hostName;
        this._mode     = msg.mode === 'group' ? 'group' : 'pair';
        this._roomId   = msg.roomId || this._roomId;
        this._fillLangSelect();
        this._syncAfterAttach();
        this._render();
    }

    _onTranscript(msg) {
        if (typeof msg.text !== 'string' || !msg.text.trim()) {
            return;
        }
        if (msg.own || msg.speaker === this._you) {
            const v = this._view;
            v.own.textContent = t('interpretOwnPrefix') + msg.text + (msg.partial ? ' …' : '');
            v.own.classList.remove('hidden');
            v.own.classList.toggle('is-partial', !!msg.partial);
            return;
        }
        // Gleiche Sprache wie ich: es gibt nichts zu uebersetzen, das Original
        // ist schon meine Fassung.
        if (msg.lang === this._myLang) {
            this._setOutput(msg.text, msg.name || '', '');
            this._flashTranslating();
            return;
        }
        if (msg.seq) {
            this._originals.set(msg.seq, msg.text);
            if (this._originals.size > 40) {
                this._originals.delete(this._originals.keys().next().value);
            }
        }
    }

    _onTranslated(msg) {
        if (typeof msg.text !== 'string' || msg.lang !== this._myLang) {
            return;
        }
        const original = msg.original || this._originals.get(msg.seq) || '';
        this._originals.delete(msg.seq);
        this._setOutput(msg.text, msg.name || '', original);
        this._flashTranslating();
    }

    _onAudio(msg) {
        if (!this._speak || typeof msg.audio !== 'string' || msg.lang !== this._myLang) {
            return;
        }
        const restMs = this._audio.play(msg.audio, msg.sampleRate);
        this._playing = true;
        this._renderStatus();
        clearTimeout(this._listenTimer);
        this._listenTimer = setTimeout(() => {
            this._playing = false;
            this._renderStatus();
        }, restMs + 200);
    }

    _onMicState(msg) {
        this._micReady  = !!(msg.on && msg.ready);
        this._micReason = msg.reason || '';
        if (!msg.on) {
            this._micWanted = false;
            this._audio.stopMic();
            if (msg.reason === 'floor_busy') this._toast(t('interpretStateFloorBusy'));
            if (msg.reason === 'unavailable') this._toast(t('interpretStateUnavailable'));
            if (msg.reason === 'error') this._toast(t('interpretStateUnavailable'));
        }
        this._audio.setStreaming(this._micReady);
        this._render();
    }

    _onEnded(msg) {
        const reason = msg.reason || 'ended';
        const key = {
            kicked      : 'interpretEndedKicked',
            host_gone   : 'interpretEndedHostGone',
            max_duration: 'interpretEndedMax',
            taken_over  : 'interpretEndedTakenOver',
        }[reason] || 'interpretEnded';
        this._toast(t(key));
        this.teardown(reason);
    }

    _onError(msg) {
        const code = msg.code || '';
        console.warn('[interpret] Fehler code=' + code);
        if (code === 'not_allowed') {
            this._toast(t('interpretNotAllowed'));
            this.teardown('not_allowed');
            return;
        }
        if (code === 'floor_busy') {
            this._toast(t('interpretStateFloorBusy'));
            return;
        }
        if (code === 'not_owner' || code === 'bad_request') {
            this._toast(t('interpretEnded'));
            this.teardown(code);
        }
    }

    // -- Bedienung ------------------------------------------------

    async toggleMic() {
        if (this._micWanted) {
            this._micWanted = false;
            this._micReady = false;
            this._audio.stopMic();
            this._send({ type: 'interpret-mic', on: false });
            this._render();
            return;
        }
        this._audio.unlock();
        const ok = await this._audio.startMic();
        if (!ok) {
            this._setStatus(t('interpretErrNoMic'), 'error');
            return;
        }
        this._micWanted = true;
        this._micReady = false;
        this._audio.setStreaming(false);
        this._send({ type: 'interpret-mic', on: true });
        this._render();
    }

    sendText() {
        const input = this._view.textInput;
        const text = (input.value || '').trim();
        if (!text) {
            return;
        }
        this._audio.unlock();
        this._send({ type: 'interpret-text', text: text.slice(0, 500) });
        input.value = '';
    }

    _setSpeak(on) {
        this._speak = !!on;
        try { localStorage.setItem(VCS.InterpretSession.SPEAK_STORAGE_KEY, this._speak ? '1' : '0'); } catch (_) {}
        if (this._speak) {
            this._audio.unlock();
        } else {
            this._audio.silence();
        }
        this._send({ type: 'interpret-speak', on: this._speak });
    }

    _loadSpeak() {
        try {
            const raw = localStorage.getItem(VCS.InterpretSession.SPEAK_STORAGE_KEY);
            if (raw !== null) this._speak = raw !== '0';
        } catch (_) {}
        if (this._view) this._view.speak.checked = this._speak;
        // Der Server nimmt "an" an -- ein Aus geht nach dem Raumeintritt raus
        // (_syncAfterAttach), vorher kennt er dieses Geraet noch nicht.
        this._speakSent = false;
    }

    _changeLang(lang) {
        if (!lang || lang === this._myLang) {
            return;
        }
        this._myLang = lang;
        this._send({ type: 'interpret-lang', lang });
        this._setOutput('', '', '');
        this._render();
    }

    // -- QR-Code (Gastgeber, Fall "am Tisch") ---------------------

    showQr(url) {
        const v = this._view;
        this._qrUrl = url || this._qrUrl;
        if (!this._qrUrl || typeof window.qrcode !== 'function') {
            console.warn('[interpret] QR nicht moeglich (url oder Encoder fehlt)');
            return false;
        }
        try {
            const qr = window.qrcode(0, 'M');
            qr.addData(this._qrUrl);
            qr.make();
            v.qrImg.innerHTML = qr.createImgTag(6, 12);
            const img = v.qrImg.querySelector('img');
            if (img) {
                img.alt = 'QR';
                img.style.width = '220px';
                img.style.height = '220px';
            }
        } catch (err) {
            console.error('[interpret] QR-Fehler: ' + (err?.message || err));
            return false;
        }
        v.qr.classList.remove('hidden');
        v.inviteQr.classList.add('is-on');
        return true;
    }

    _hideQr() {
        if (!this._view) return;
        this._view.qr.classList.add('hidden');
        this._view.inviteQr.classList.remove('is-on');
    }

    toggleQr(url) {
        if (!this._view.qr.classList.contains('hidden')) {
            this._hideQr();
            return;
        }
        this.showQr(url);
    }

    // -- Darstellung ----------------------------------------------

    _bindView() {
        if (this._bound) {
            return;
        }
        const $ = (id) => document.getElementById(id);
        this._view = {
            overlay      : $('interpretOverlay'),
            close        : $('interpretClose'),
            title        : $('interpretTitle'),
            membersBtn   : $('interpretMembersBtn'),
            membersCount : $('interpretMembersCount'),
            membersList  : $('interpretMembers'),
            inviteBar    : $('interpretInviteBar'),
            inviteLink   : $('interpretInviteLink'),
            inviteQr     : $('interpretInviteQr'),
            inviteContact: $('interpretInviteContact'),
            qr           : $('interpretQr'),
            qrImg        : $('interpretQrImg'),
            outputWrap   : $('interpretOutputWrap'),
            speaker      : $('interpretSpeaker'),
            output       : $('interpretOutput'),
            original     : $('interpretOriginal'),
            own          : $('interpretOwn'),
            status       : $('interpretStatus'),
            textForm     : $('interpretTextForm'),
            textInput    : $('interpretTextInput'),
            speak        : $('interpretSpeak'),
            langSelect   : $('interpretLang'),
            mic          : $('interpretMic'),
            micLabel     : $('interpretMicLabel'),
            brand        : $('interpretBrand'),
        };
        const v = this._view;
        if (!v.overlay) {
            console.warn('[interpret] #interpretOverlay fehlt');
            return;
        }
        v.close.addEventListener('click', () => void this.requestClose());
        v.mic.addEventListener('click', () => void this.toggleMic());
        v.textForm.addEventListener('submit', (e) => {
            e.preventDefault();
            this.sendText();
        });
        v.speak.addEventListener('change', () => this._setSpeak(v.speak.checked));
        v.langSelect.addEventListener('change', () => this._changeLang(v.langSelect.value));
        v.membersBtn.addEventListener('click', () => {
            v.membersList.classList.toggle('hidden');
            this._renderMembers();
        });
        v.inviteLink?.addEventListener('click', () => this._opts.onInviteLink?.());
        v.inviteQr?.addEventListener('click', () => this._opts.onInviteQr?.());
        v.inviteContact?.addEventListener('click', () => this._opts.onInviteContact?.());
        v.membersList.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-kick]');
            if (!btn) return;
            this._send({ type: 'interpret-kick', memberId: btn.dataset.kick });
        });
        this._bound = true;
    }

    _others() {
        return this._members.filter(m => m.id !== this._you && m.connected);
    }

    _render() {
        if (!this._view) return;
        const v = this._view;
        const connected = this._members.filter(m => m.connected).length || 1;
        v.membersCount.textContent = String(connected);
        v.title.textContent = t(this._mode === 'group' ? 'interpretTitleGroup' : 'interpretTitle');

        // Mikro-Knopf. In der Gruppe meldet sich ein Zuhoerer zu Wort.
        const listener = this._mode === 'group' && this._role !== 'host';
        const labelKey = this._micWanted
            ? (listener ? 'interpretFloorOff' : 'interpretMicOff')
            : (listener ? 'interpretFloorOn' : 'interpretMicOn');
        v.micLabel.textContent = t(labelKey);
        v.mic.classList.toggle('is-running', this._micWanted);
        // Jemand anderes hat das Wort: Knopf sperren, Schreiben auch.
        const floorTaken = listener && this._floor && this._floor !== this._you;
        v.mic.disabled = !!floorTaken && !this._micWanted;
        v.textInput.disabled = !!floorTaken;

        if (v.output.classList.contains('is-placeholder')) {
            v.output.textContent = this._placeholderText();
        }
        this._renderStatus();
        if (!v.membersList.classList.contains('hidden')) this._renderMembers();
    }

    _placeholderText() {
        const others = this._others();
        if (!others.length) {
            if (this._role === 'host') {
                return t(this._mode === 'group' ? 'interpretWaitingGroup' : 'interpretWaitingHost');
            }
            return t('interpretWaitingPartner').replace('{name}', this._hostName || t('guest'));
        }
        return t('interpretReady');
    }

    _renderStatus() {
        if (!this._view) return;
        if (this._playing) {
            this._setStatus(t('interpretStateSpeaking'), 'translating');
            return;
        }
        if (this._micWanted && this._micReason === 'alone') {
            this._setStatus(t('interpretStateAlone'), 'idle');
            return;
        }
        if (this._micWanted && !this._micReady) {
            this._setStatus(t('interpretStateStarting'), 'idle');
            return;
        }
        if (this._micWanted && this._micReady) {
            this._setStatus(t('interpretStateListening'), 'listening');
            return;
        }
        const listener = this._mode === 'group' && this._role !== 'host';
        if (listener && this._floor && this._floor !== this._you) {
            this._setStatus(t('interpretStateFloorBusy'), 'idle');
            return;
        }
        this._setStatus(t('interpretStateMicOff'), 'idle');
    }

    _setStatus(text, kind) {
        const el = this._view?.status;
        if (!el) return;
        el.textContent = text;
        el.classList.remove('is-idle', 'is-listening', 'is-translating', 'is-error');
        el.classList.add('is-' + (kind || 'idle'));
        this._view.outputWrap?.classList.toggle('is-listening', kind === 'listening');
    }

    _flashTranslating() {
        if (this._playing) return;
        this._setStatus(t('liveTalkStateTranslating'), 'translating');
        clearTimeout(this._listenTimer);
        this._listenTimer = setTimeout(() => this._renderStatus(), VCS.InterpretSession.LISTEN_AGAIN_MS);
    }

    _setOutput(text, speaker, original) {
        const v = this._view;
        if (!v) return;
        if (!text) {
            v.output.textContent = this._placeholderText();
            v.output.classList.add('is-placeholder');
            v.speaker.textContent = '';
            v.original.textContent = '';
            return;
        }
        v.output.textContent = text;
        v.output.classList.remove('is-placeholder');
        v.speaker.textContent = speaker;
        v.original.textContent = original;
        this._fitOutput();
    }

    _fitOutput() {
        const el = this._view.output;
        let size = VCS.InterpretSession.OUTPUT_MAX_PX;
        el.style.fontSize = size + 'px';
        let guard = 0;
        while (size > VCS.InterpretSession.OUTPUT_MIN_PX
               && (el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1)
               && guard < 40) {
            size -= 1;
            el.style.fontSize = size + 'px';
            guard++;
        }
        el.scrollTop = el.scrollHeight;
    }

    _renderMembers() {
        const v = this._view;
        const isHost = this._role === 'host';
        const rows = this._members.map(m => {
            const me = m.id === this._you;
            const name = escapeHtml(m.name || t('guest')) + (me ? ' ' + escapeHtml(t('interpretYou')) : '');
            const role = m.kind === 'host' ? ' · ' + escapeHtml(t('interpretHost')) : '';
            const lang = escapeHtml((typeof LANG_NAMES !== 'undefined' && LANG_NAMES[m.lang]) || m.lang.toUpperCase());
            const mic = m.mic ? '<span class="interpret-member-mic" aria-hidden="true">●</span>' : '';
            const off = m.connected ? '' : ' is-offline';
            const kick = isHost && !me ? `<button type="button" class="interpret-member-kick" data-kick="${escapeHtml(m.id)}">${escapeHtml(t('interpretKick'))}</button>` : '';
            return `<div class="interpret-member${off}">${mic}<span class="interpret-member-name">${name}</span><span class="interpret-member-lang">${lang}${role}</span>${kick}</div>`;
        });
        v.membersList.innerHTML = `<div class="interpret-members-title">${escapeHtml(t('interpretMembers'))}</div>` + rows.join('');
    }

    _fillLangSelect() {
        const sel = this._view?.langSelect;
        if (!sel || typeof AVAILABLE_LANGS === 'undefined') return;
        if (!sel.options.length) {
            for (const code of AVAILABLE_LANGS) {
                const opt = document.createElement('option');
                opt.value = code;
                opt.textContent = LANG_NAMES[code] || code.toUpperCase();
                sel.appendChild(opt);
            }
        }
        sel.value = this._myLang;
    }

    // -- Kleine Helfer --------------------------------------------

    _toast(text) {
        if (VCS.ui?.showToast) {
            VCS.ui.showToast(escapeHtml(text), 'info', 4000);
            return;
        }
        this._opts.onToast?.(text);
    }

    _confirm(text) {
        if (VCS.ui?.showConfirm) {
            try {
                const r = VCS.ui.showConfirm(text);
                return (r && typeof r.then === 'function') ? r : Promise.resolve(!!r);
            } catch (_) {}
        }
        return Promise.resolve(window.confirm(text));
    }
};
